import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { EventType, Router } from '@angular/router';
import { Subject, Subscription, timer } from 'rxjs';

import { TimeDiffPipe } from '../../pipes/time-diff/time-diff.pipe';
import { AuthApi } from '../auth-api/auth-api.service';
import { ToastNotificationsManagerService } from '../toast-notifications-manager/toast-notifications-manager.service';

@Injectable({
  providedIn: 'root',
})
export class IdleUserService implements OnDestroy {
  private idleTimesSubscription: Subscription;
  private IDLE_TIME = 10 * 60; // default: 10 minutes
  sessionExpireMs: number; // expiry time in ms
  isIdleToastVisible: boolean;
  isResetting = false;

  idlePercentage: number; // percentage of idle time left, 100% at start, 0% at end
  sessionExpiresDiff: number; // time in seconds for JWT to expire, to be used in timeDiff pipe
  userInactive = new Subject<boolean>();

  constructor(
    readonly authApi: AuthApi,
    private router: Router,
    private toastNotif: ToastNotificationsManagerService,
  ) {
    // extract session expiry and idle time from local storage
    const ls = this.authApi.returnLocalStorage();
    if (ls?.expiry && this.isInt(ls.expiry)) this.sessionExpireMs = ls.expiry;
    if (ls?.inactivityLogoutIntervalInMinutes && this.isInt(ls.inactivityLogoutIntervalInMinutes))
      this.IDLE_TIME = ls?.inactivityLogoutIntervalInMinutes * 60;

    this.startIdleTimer();

    // reset timer on route change
    this.router.events.subscribe(val => {
      if (val.type === EventType.NavigationEnd) this.resetIdleTimer();
    });
    // reset timer on input events
    ['mousemove', 'keypress', 'touchend'].forEach(event =>
      window.addEventListener(event, () => this.resetIdleTimer()),
    );
  }

  // resetIdleTimer() will be called on any activity detected
  resetIdleTimer() {
    // allow one reset every 10s
    if (this.isResetting) return;
    this.isResetting = true;
    setTimeout(() => (this.isResetting = false), 10 * 1000);

    this.toastNotif.removeNotif('idle-logout');
    this.isIdleToastVisible = false;
    this.startIdleTimer();
  }

  startIdleTimer() {
    // remove timer if already running
    if (this.idleTimesSubscription) this.idleTimesSubscription.unsubscribe();
    // create timer
    this.idleTimesSubscription = timer(0, 1000).subscribe(t => {
      // calculate percentage of time passed. reversed, ie. 100% at start, 0% at end
      this.idlePercentage = 100 - (t * 100) / this.IDLE_TIME;
      // calculate session time difference
      this.sessionExpiresDiff = (this.sessionExpireMs - Date.now()) / 1000;

      // show toast at 20% time left (20% of 15 minutes is  3 minutes)
      if (this.idlePercentage <= 20 && !this.isIdleToastVisible) {
        this.toastNotif.addNew({
          id: 'idle-logout',
          type: 'warning',
          content: `You are going to be logged out due to inactivity. Move your mouse or press any key to stay logged in.`,
          hideAfterMs: 0,
        });
        this.isIdleToastVisible = true;
      }

      // time is up, remove timer, notify user, logout
      if (this.idlePercentage == 0) {
        if (this.idleTimesSubscription) this.idleTimesSubscription.unsubscribe();
        this.userInactive.next(true);
      }
    });
  }

  isInt(n: any): boolean {
    return Number(n) === n && n % 1 === 0;
  }

  ngOnDestroy() {
    if (this.idleTimesSubscription) this.idleTimesSubscription.unsubscribe();
  }
}
